import React, { useState, useEffect } from 'react';
import { useFamily } from '../context/FamilyContext';
import { usePermissions } from '../hooks/usePermissions';
import { subscribeToExpenses, subscribeToRecurringExpenses } from '@coparent/shared/src/services/expenseService';
import ExpenseList from '../components/expenses/ExpenseList';
import ExpenseForm from '../components/expenses/ExpenseForm';
import RecurringExpenseList from '../components/expenses/RecurringExpenseList';
import RecurringExpenseForm from '../components/expenses/RecurringExpenseForm';
import BalanceSummary from '../components/expenses/BalanceSummary';
import BulkSettleModal from '../components/expenses/BulkSettleModal';
import { HiPlus, HiCash, HiRefresh } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import NotificationBell from '../components/notifications/NotificationBell';

const ExpensesPage = () => {
  const { familyId } = useFamily();
  const { canSettle } = usePermissions();
  const [expenses, setExpenses] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'pending' | 'settled'
  const [showBulkSettle, setShowBulkSettle] = useState(false);
  const [tab, setTab] = useState('expenses'); // 'expenses' | 'recurring'

  useEffect(() => {
    if (!familyId) return;
    const unsubs = [
      subscribeToExpenses(familyId, setExpenses),
      subscribeToRecurringExpenses(familyId, setRecurringExpenses),
    ];
    return () => unsubs.forEach(u => u());
  }, [familyId]);

  const filteredExpenses = expenses.filter(e => {
    if (filter === 'pending') return e.status === 'pending';
    if (filter === 'settled') return e.status === 'settled';
    return true;
  });

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingExpense(null);
  };

  const handleEditRecurring = (re) => {
    setEditingRecurring(re);
    setShowRecurringForm(true);
  };

  const handleCloseRecurring = () => {
    setShowRecurringForm(false);
    setEditingRecurring(null);
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between page-header">
        <div>
          <h1 className="page-title">Expenses</h1>
          <p className="page-subtitle">Track and split shared costs</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/payments"
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <HiCash className="text-base" />
            Payment History
          </Link>
          {tab === 'expenses' && (
            <button
              onClick={() => { setEditingExpense(null); setShowForm(true); }}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <HiPlus className="text-base" />
              Add Expense
            </button>
          )}
          {tab === 'recurring' && (
            <button
              onClick={() => { setEditingRecurring(null); setShowRecurringForm(true); }}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <HiPlus className="text-base" />
              Add Recurring
            </button>
          )}
          <NotificationBell />
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab('expenses')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            tab === 'expenses'
              ? 'bg-ink-800 text-white'
              : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
          }`}
        >
          <HiCash className="text-base" />
          Expenses
        </button>
        <button
          onClick={() => setTab('recurring')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            tab === 'recurring'
              ? 'bg-ink-800 text-white'
              : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
          }`}
        >
          <HiRefresh className="text-base" />
          Recurring
          {recurringExpenses.length > 0 && (
            <span className={`text-[0.625rem] px-1.5 py-0.5 rounded-full font-bold ${
              tab === 'recurring' ? 'bg-white/20 text-white' : 'bg-ink-200 text-ink-600'
            }`}>
              {recurringExpenses.length}
            </span>
          )}
        </button>
      </div>

      {tab === 'expenses' && (
        <>
          <div className="mb-6">
            <BalanceSummary expenses={expenses} onSettleUp={canSettle ? () => setShowBulkSettle(true) : undefined} />
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-5">
            {['all', 'pending', 'settled'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={filter === f ? 'chip-active' : 'chip-inactive'}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <ExpenseList expenses={filteredExpenses} onEdit={handleEdit} />
        </>
      )}

      {tab === 'recurring' && (
        <RecurringExpenseList
          recurringExpenses={recurringExpenses}
          onEdit={handleEditRecurring}
        />
      )}

      {showForm && <ExpenseForm onClose={handleClose} expense={editingExpense} />}
      {showRecurringForm && <RecurringExpenseForm onClose={handleCloseRecurring} recurringExpense={editingRecurring} />}
      {showBulkSettle && <BulkSettleModal expenses={expenses} onClose={() => setShowBulkSettle(false)} />}
    </div>
  );
};

export default ExpensesPage;
